import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClientSettings } from '../../contexts/ClientSettingsContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from '../LanguageSelector';
import {
  LayoutDashboard,
  Users,
  Bot,
  DollarSign,
  FileText,
  LogOut,
  ChevronRight,
  BarChart3,
  Gauge,
  Plug,
  Lock,
  Phone,
  Megaphone,
  PhoneCall,
  PhoneOff,
  Activity,
  AudioWaveform,
  Mic2,
  FolderOpen,
  UserCircle,
  Settings,
  BookOpen,
  Radio
} from 'lucide-react';

const ClientSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientUser, clientLogout } = useAuth();
  const { hasFeature, loading, planName } = useClientSettings();
  const { t } = useLanguage();

  // Menu items with feature requirements - organized by section
  const menuSections = [
    {
      title: t('sidebar.voiceAiSetup'),
      items: [
        { icon: Bot, label: t('nav.voiceAgents'), path: '/client/agents', feature: null },
        { icon: Mic2, label: t('nav.cloneVoice'), path: '/client/clone-voice', feature: null, highlight: true, isNew: true },
        { icon: FolderOpen, label: t('nav.files'), path: '/client/files', feature: null },
        { icon: Plug, label: t('nav.integrations'), path: '/client/integrations', feature: 'integrations_marketplace' },
      ]
    },
    {
      title: t('sidebar.operationsMonitoring'),
      items: [
        { icon: Phone, label: t('sidebar.phoneNumbers'), path: '/client/dialer', feature: null },
        { icon: Megaphone, label: t('nav.campaigns'), path: '/client/campaigns', feature: null },
        { icon: UserCircle, label: t('nav.leads'), path: '/client/leads', feature: null, highlight: true, isNew: true },
        { icon: FileText, label: t('sidebar.callLogs'), path: '/client/reports', feature: 'analytics_dashboard' },
        { icon: BarChart3, label: t('nav.analytics'), path: '/client/analytics', feature: null, highlight: true },
      ]
    },
    {
      title: t('sidebar.monitoring'),
      items: [
        { icon: Activity, label: t('nav.agentMonitor'), path: '/client/agent-monitor', feature: null, highlight: true },
        { icon: Activity, label: t('sidebar.campaignHealth'), path: '/client/campaigns/health', feature: null, highlight: true },
        { icon: AudioWaveform, label: t('sidebar.voiceAnalytics'), path: '/client/voice-analytics', feature: null, highlight: true },
      ]
    },
    {
      title: t('sidebar.management'),
      items: [
        { icon: LayoutDashboard, label: t('nav.dashboard'), path: '/client/dashboard', feature: null },
        { icon: Gauge, label: t('sidebar.usageQuotas'), path: '/client/usage', feature: null },
        { icon: Users, label: t('nav.users'), path: '/client/users', feature: null },
        { icon: PhoneOff, label: t('nav.dnc'), path: '/client/dnc', feature: null },
        { icon: PhoneCall, label: t('sidebar.dialerPortal'), path: '/client/dialer-portal', feature: null, highlight: true },
      ]
    },
    {
      title: t('sidebar.accountBilling'),
      items: [
        { icon: DollarSign, label: t('nav.billing'), path: '/client/billing', feature: null },
        { icon: Radio, label: t('sidebar.telnyxSettings'), path: '/client/telnyx-settings', feature: null, highlight: true, isNew: true },
        { icon: Settings, label: t('nav.settings'), path: '/client/settings', feature: null },
        { icon: BookOpen, label: t('nav.docs'), path: '/client/docs', feature: null },
      ]
    },
  ];

  const handleLogout = () => {
    clientLogout();
    navigate('/client');
  };

  return (
    <div className="w-64 bg-[#0d1117] border-r border-gray-800 min-h-screen flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-gray-800">
        <a href="/" className="text-xl font-bold text-white tracking-tight">
          AI<span className="text-teal-400">CONNEQTORS</span>
        </a>
        <p className="text-gray-500 text-xs mt-1">{t('sidebar.clientPortal')}</p>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">
            {clientUser?.name?.charAt(0).toUpperCase() || 'C'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm truncate">{clientUser?.name || t('sidebar.client')}</p>
            <p className="text-blue-400 text-xs">{t('sidebar.clientAdmin')}</p>
          </div>
        </div>
        {/* Plan Badge */}
        {!loading && planName && (
          <div className="mt-3 px-3 py-1.5 bg-teal-400/10 border border-teal-400/30 rounded-lg">
            <p className="text-teal-400 text-xs font-medium text-center">{planName} {t('sidebar.plan')}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {menuSections.map((section) => {
          const visibleItems = section.items.filter(item => {
            if (!item.feature) return true;
            return hasFeature(item.feature);
          });
          
          if (visibleItems.length === 0) return null;
          
          return (
            <div key={section.title} className="mb-6">
              <p className="text-gray-500 text-[10px] font-semibold tracking-wider mb-2 px-2">
                {section.title}
              </p>
              <ul className="space-y-1">
                {visibleItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <button
                        onClick={() => navigate(item.path)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                          isActive
                            ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                            : item.highlight 
                              ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-400/10 border border-transparent'
                              : 'text-gray-400 hover:text-white hover:bg-[#1a1a1f] border border-transparent'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="flex-1 text-left text-sm">{item.label}</span>
                        {item.isNew && !isActive && <span className="text-[9px] px-1.5 py-0.5 bg-purple-400/20 text-purple-400 rounded">{t('common.new')}</span>}
                        {isActive && <ChevronRight className="w-4 h-4" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}

        {/* Disabled Features Notice */}
        {!loading && (
          <div className="mt-2 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 text-xs">
              <Lock className="w-3 h-3" />
              <span>{t('sidebar.restrictedFeatures')}</span>
            </div>
          </div>
        )}
      </nav>

      {/* Language Selector & Logout */}
      <div className="p-4 border-t border-gray-800 space-y-3">
        <LanguageSelector variant="compact" className="justify-center" />
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-all duration-200"
          data-testid="client-logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm font-medium">{t('common.logout')}</span>
        </button>
      </div>
    </div>
  );
};

export default ClientSidebar;
